import React, { useEffect, useMemo, useRef, useState } from "react";
import { GradFlow } from "gradflow";
import { createAvatar } from "@dicebear/core";
import { identicon } from "@dicebear/collection";

import { ACHIEVEMENT_GRADFLOW_CONFIG } from "../utils/heroGradient";

/* Mirrors the whitelist in server/routes/posts.js. The value is the only part
   that crosses the wire; the label is ours to word. */
const PERIODS = [
  { value: "month", label: "This month" },
  { value: "lastmonth", label: "Last month" },
  { value: "12months", label: "Last 12 months" },
  { value: "all", label: "All time" },
];

/**
 * The three people who filed the most reports in a period, on the home page.
 *
 * Owns its own fetch and its own section so Home.jsx gains one line, the way
 * CategoryStrip and RvrRoadmap do.
 *
 * Names come from the server already resolved, and anonymous accounts arrive as
 * their Bleeper alias -- which is most of them, since accounts are created
 * anonymous by default. That is the identity their posts already carry
 * everywhere else on the page, so the leaderboard agrees with the feed above it
 * rather than quietly ranking a different set of people.
 */
const TopContributors = () => {
  /* Null until the first response: the opening period is the server's to pick,
     since only it knows which windows have anyone in them. Every change after
     that is an explicit request for exactly that period. */
  const [period, setPeriod] = useState(null);
  const [entry, setEntry] = useState(null);
  const [busy, setBusy] = useState(true);
  const [failed, setFailed] = useState(false);

  /* Switching back to a period already seen should not hit the API again. */
  const cacheRef = useRef(new Map());

  useEffect(() => {
    const cached = period && cacheRef.current.get(period);
    if (cached) {
      setEntry(cached);
      setBusy(false);
      return;
    }

    let cancelled = false;
    setBusy(true);

    /* No period on the first request. The server then walks this month, last
       month, 12 months, all time and answers with the first that has anyone,
       so a quiet month opens on last month instead of on an empty section --
       in one round trip rather than up to four from here. */
    fetch(
      `/api/posts/topcontributors${
        period ? `?period=${encodeURIComponent(period)}` : ""
      }`,
    )
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        /* The server echoes the period it actually ran. On the first request
           that is its choice, which the dropdown then adopts; later it guards
           against a slow response landing after a faster one. */
        const resolved = data?.period;
        if (period && resolved && resolved !== period) return;

        const next = {
          contributors: Array.isArray(data?.contributors)
            ? data.contributors
            : [],
          periodLabel: data?.periodLabel || "",
        };
        if (resolved) cacheRef.current.set(resolved, next);

        setEntry(next);
        setBusy(false);
        if (!period && resolved) setPeriod(resolved);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching top contributors:", err);
        setFailed(true);
        setBusy(false);
      });

    return () => {
      cancelled = true;
    };
  }, [period]);

  /* Same generator and the same seed as the feed cards (components/Post.jsx),
     so a contributor's face here is the one on their own bleeps. Home's own
     greeting avatar goes over the network to api.dicebear.com; three more
     round trips for something the bundle already produces is not worth it. */
  const avatars = useMemo(() => {
    const map = new Map();
    (entry?.contributors ?? []).forEach(({ displayname }) => {
      if (map.has(displayname)) return;
      map.set(
        displayname,
        createAvatar(identicon, {
          seed: displayname,
          backgroundType: ["gradientLinear", "solid"],
        }).toDataUri(),
      );
    });
    return map;
  }, [entry]);

  /* The section is never hidden for want of data -- an empty period renders its
     own line below, with the dropdown still there. It stays unmounted only
     before the first response and if that request failed outright, where a
     broken empty band would say less than nothing. */
  if (failed || !entry) return null;

  const { contributors, periodLabel } = entry;

  /* Its own brand band, so it reads as neither the dark feed above it nor the
     off-white how-it-works below. The canvas runs a different gradient from the
     hero's: see ACHIEVEMENT_GRADFLOW_CONFIG for why it is a slow sheen rather
     than the hero's weather. */
  return (
    <section className="bl-section bl-contrib-section">
      <GradFlow
        className="bl-contrib__gradflow"
        config={ACHIEVEMENT_GRADFLOW_CONFIG}
      />

      <div className="bl-shell">
        <div className="bl-section-head">
          {/* The flip stops here, exactly as it does on /report. The cards and
              the select beside them need the ordinary light scale for their
              opaque surfaces and dark text; carrying .bl-on-brand across the
              whole band would hand them glass surfaces over an animated
              canvas, which is the 2.3:1 case the theme layer warns about. */}
          <div className="bl-on-brand">
            <h2 className="bl-section-title bl-contrib__title">
              <i
                className="fa-solid fa-crown"
                aria-hidden="true"
              />
              Top contributors
            </h2>
            {/* The label alone. The title says what is being ranked and the
                dropdown says how it is being filtered, so a lead that repeats
                either only leaves somewhere to hang a dash. */}
            <p className="bl-section-lead">
              {periodLabel || "Most reports filed"}
            </p>
          </div>

          {/* A native select, the same one DocPage uses for its jump menu: four
              options on a phone is exactly what the platform control is for. */}
          <div className="bl-contrib__period">
            <label
              className="bl-sr-only"
              htmlFor="contrib-period"
            >
              Period
            </label>
            <select
              id="contrib-period"
              className="bl-input"
              value={period || "month"}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIODS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {contributors.length === 0 ? (
          <p className="bl-contrib__empty">
            No BLEEPs were posted in this period.
          </p>
        ) : (
          /* An ordered list: the ranking is the content, not decoration. */
          <ol
            className="bl-contrib"
            aria-busy={busy ? "true" : "false"}
          >
            {contributors.map((contributor, index) => (
              <li
                key={contributor.id}
                className={`bl-contrib__card${
                  index === 0 ? " bl-contrib__card--first" : ""
                }`}
              >
                <span
                  className="bl-contrib__rank"
                  aria-hidden="true"
                >
                  {index + 1}
                </span>
                <span className="bl-contrib__avatar">
                  <img
                    src={avatars.get(contributor.displayname)}
                    alt=""
                  />
                </span>
                <span className="bl-contrib__body">
                  <span className="bl-contrib__name">
                    {contributor.displayname}
                  </span>
                  <span className="bl-contrib__count">
                    {contributor.count}{" "}
                    {contributor.count === 1 ? "BLEEP" : "BLEEPs"}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
};

export default TopContributors;
